import React, { Component } from 'react';
import './style.css';
import { Select, Input, Icon, Tooltip, Form, Row, Col, Spin, DatePicker } from 'antd';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import * as actions from "../../actions/index";
import { compose } from "recompose";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { nationalAwards, REMOVED_SEARCHFILTERS, filters, OPERATOR_MAP, OPERATOR_NORMALIZER, BooleanFilters, DB_to_UI } from '../../auth';

import InputMask from "react-input-mask";
import moment from "moment";
import memoizeOne from "memoize-one";

import LoadingModal from '../Inc/LoadingModal'
import { bool } from 'prop-types';

const LoadingIcon = <Icon type="loading" style={{ fontSize: 24 }} spin />;

const FormItem = Form.Item;
const Option = Select.Option;

const { RangePicker } = DatePicker


let uuid = 0;
let councilNumberFieldKey = null;
let sortedCouncils = [];
let filteredNationalAwards = [];

// Only offer the subset of the akelaAwards lookup that matches our national awards list (auth.js) -
// the lookup's awardLong values don't always match those exactly, so match on either containing the other.
function getFilteredNationalAwards(apiNationalAwards) {
    if (!Array.isArray(apiNationalAwards)) return [];

    return apiNationalAwards.filter(apiAward =>
        nationalAwards.some(nationalAward =>
            nationalAward.includes(apiAward.awardLong) ||
            apiAward.awardLong.includes(nationalAward)
        )
    );
}


const FILTER_KEYS_LOWER = new Set(filters.map(f => f.toLowerCase()));
// Same keys with underscores dropped, so "nesa_state" resolves to the "NESAState" filter.
const FILTER_KEYS_NO_UNDERSCORE = new Set(filters.map(f => f.toLowerCase().replace(/_/g, '')));

// Filter keys are inconsistently cased (e.g. "FirstNameBV" and "firstnamebv"), so suffixes are
// matched case-insensitively. "...BV" is ambiguous with "...NBV" when the column itself ends in "n"
// (e.g. "councilaffiliationbv"), so a trailing "nbv" only counts as NBV if the remaining
// base column is itself a known filter key.
function matchesSuffix(key, suffix) {
    const lowerKey = key.toLowerCase();
    const lowerSuffix = suffix.toLowerCase();

    if (!lowerKey.endsWith(lowerSuffix)) return false;

    if (lowerSuffix === 'nbv' || lowerSuffix === 'bv') {
        const base = (lowerSuffix === 'nbv')
            ? lowerKey.slice(0, -3)
            : lowerKey.slice(0, -2);
        const nbvBase = lowerKey.endsWith('nbv') ? lowerKey.slice(0, -3) : null;
        // A column with no plain key (e.g. "ProgramID" only has InList/BV/NBV) is still a known base if it has a sibling BV/InList key.
        const isNbv = nbvBase !== null && (FILTER_KEYS_LOWER.has(nbvBase) || FILTER_KEYS_LOWER.has(nbvBase + 'bv') || FILTER_KEYS_LOWER.has(nbvBase + 'inlist') ||FILTER_KEYS_LOWER.has(nbvBase.replace(/_$/, '')) || FILTER_KEYS_NO_UNDERSCORE.has(nbvBase.replace(/_/g, '')));
        return lowerSuffix === 'nbv' ? isNbv : (!isNbv && !!base);
    }

    return true;
}

function parseFilterKey(key) {
    // Longest suffix wins so compound suffixes ("endswithdoesnotmatch") aren't swallowed
    // by a shorter one they end with ("doesnotmatch").
    const suffix = Object.keys(OPERATOR_MAP)
      .filter(s => matchesSuffix(key, s))
      .sort((a, b) => b.length - a.length)[0];

    if (suffix) {
      let col = key.slice(0, key.length - suffix.length); // preserve original case
      col = DB_to_UI[col] ? DB_to_UI[col] : col
      return {
        column: col.endsWith('_') ? col.slice(0, col.length - 1) : col,
        operator: OPERATOR_MAP[suffix]
      };
    }

    return { column: key, operator: "Equals" };
}

function buildColumnOperatorMap(filters) {
    const map = {};

    filters.forEach(f => {
        const parsed = parseFilterKey(f);
        const { operator } = parsed;
        // ID filters (AffinityGroupID, ...) contribute their operators to the base column.
        const column = parsed.column.replace(/^(AffinityGroup|ScoutingAffiliation)ID$/i, '$1');

        if (!map[column.toLowerCase()]) map[column.toLowerCase()] = new Set();
        map[column.toLowerCase()].add(operator);
    });

    Object.keys(map).forEach(col => {
        map[col] = [...map[col]].map(op => OPERATOR_MAP[op] || op);
    });

    return map;
}

function getOperatorsForColumn(column, map) {
    return map[column] || ["EQ"];
}

function normalizeIncomingOperator(op) {
    return OPERATOR_NORMALIZER[op] || op;
}

function isInListOperator(fieldiddata, k) {
    // "_range" doubles as a multi-select operator for select-based columns like
    // EagleScoutUnitState (date/zip columns use "_range" too, but render via
    // RangePicker/Input instead of this Select, so they're unaffected).
    return !!(fieldiddata && (fieldiddata[k + "3"] === "INLIST" || fieldiddata[k + "3"] === "_range"));
}

// AffinityGroup / ScoutingAffiliation send the lookup ID to the API (AffinityGroupID /
// ScoutingAffiliationID) for exact-match operators; text operators (contains, starts with, ...)
// still work on the name.
const ID_VALUE_OPERATORS = ['eq', 'equals', 'inlist', 'doesnotmatch', 'ne'];

function usesIdValue(fieldiddata, k) {
    return ID_VALUE_OPERATORS.includes(String((fieldiddata && fieldiddata[k + "3"]) || '').toLowerCase().replace(/\s/g, ''));
}

function getLookupId(item) {
    if (item.id !== undefined && item.id !== null) return item.id;
    const idKey = Object.keys(item).find(key => /id$/i.test(key));
    return idKey ? item[idKey] : undefined;
}

// InList-capable dropdown filters accept multiple selections, so their form value
// must be an array once InList is the active operator (single-select otherwise).
function getFilterValueInitial(fieldiddata, k) {
    const value = fieldiddata && fieldiddata[k + "4"];

    if (isInListOperator(fieldiddata, k)) {
        if (!value) return [];
        return Array.isArray(value) ? value : [value];
    }

    return value || '';
}


const columns = [...new Set(filters.map(f => parseFilterKey(f).column))];

//Remove the filters as per Paul's email titled "Updated list" - AAG-1264(Attached in the ticket)
const removed = REMOVED_SEARCHFILTERS.map(x => x.toLowerCase());

// ID-based variants of AffinityGroup / ScoutingAffiliation are used only for querying,
// so they must not appear as separate entries in the filter dropdown.
const HIDDEN_DROPDOWN_FILTERS = ['affinitygroupid', 'scoutingaffiliationid'];

const filteredColumns = columns.filter(
    col => !removed.includes(col.toLowerCase()) && !HIDDEN_DROPDOWN_FILTERS.includes(col.toLowerCase())
);

// const filteredColumns = columns

const columnOperatorMap = buildColumnOperatorMap(filters);

const LABEL_MAP = {
    prsonguid: "PersonGUID",
    skpersonid: "MemberID",
    personid: "PersonID",
    nesa_state: 'NESAState',
    nationalawardid: 'NationalAward'
  };
  
  function getColumnLabel(col) {
    return LABEL_MAP[col] || formatColumnLabel(col);
  }

  function formatColumnLabel(col) {
    const ACRONYMS = ["ID", "GUID", "DOB", "ZIP", "FK", "SK"];
  
    let label = col
    //   .replace(/_/g, " ")  // 🔹 replace underscore with space
      // 🔹 split camelCase
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      // 🔹 fix cases like GUIDValue
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
  
      if(BooleanFilters.includes(col)) label = label.replaceAll(/_/g, " ");
      
    // 🔹 normalize words to Title Case first
    label = label
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  
    // 🔹 fix acronyms back to uppercase
    ACRONYMS.forEach((acr) => {
      const regex = new RegExp(`\\b${acr}\\b`, "gi");
      label = label.replace(regex, acr);
    });
  
    return label;
  }

class Groupfields extends React.Component {

    constructor(props) {
        super(props)
        this.state = {
            ...props,
            ViewMode: '',
            date_MaskChar: null,
            scoutingAffiliationKey: [],
            affinityGroupsKey: [],
            selectedCommunityType: {},
            selectedOrgName: {}
        }
    }

    remove = (index, k) => {
        const { getFieldValue, setFieldsValue } = this.props;
        // can use data-binding to get
        const keys = getFieldValue("groupfilter" + index);
        // We need at least one passenger
        if (keys.length === 1) {
            //return;
        }
        // can use data-binding to set
        setFieldsValue({
            ["groupfilter" + index]: keys.filter(key => key !== k)
        });

        let obj = {
            [index]: keys.filter(key => key !== k)
        }

        if(councilNumberFieldKey == k){
            this.props.checkCouncilNumberSelectedHandler(false)
        }
        this.props.updatefieldsgroup(obj);

    };

    add = (index) => {
        const { getFieldValue, setFieldsValue } = this.props;
        console.log(index)
        // can use data-binding to get
        const keys = getFieldValue("groupfilter" + index);
        let groupfilter = [];
        if (keys) {
            groupfilter = keys;
        } else {
            groupfilter = [];
        }
        const nextKeys = groupfilter.concat(uuid++);

        setFieldsValue({
            ["groupfilter" + index]: nextKeys
        });

        if (this.props.fieldsgroup[index] != index) {
            let obj = {
                [index]: nextKeys
            }
            this.props.updatefieldsgroup(obj);
        }

        console.log(this.props.groupids)
        console.log(this.props.result)


    };


    componentWillReceiveProps = (nextprops) => {

        if (!Array.isArray(this.props.orgInfo) && this.props.isCSTSupport && !nextprops.positionLoading && nextprops.positionList ) {
            const getCSTpositionOrgGuid = nextprops.positionList.find(list => list.personContext == "AlumniCSTSupport")
            if (getCSTpositionOrgGuid) this.props.actions.getChildOrganizationsGuid(getCSTpositionOrgGuid.organizationGuid);
        }

        if (this.props.result !== nextprops.result) {
            uuid = 0;

            this.props.setFieldsValue({
                ["groupfilter" + this.props.columnId[this.props.index]]: []
            });
            // if (nextprops.result && nextprops.result.length > this.props.columIdByChield.length) {
            nextprops.result[this.props.columnId[this.props.index]].map((data, i) => {
                this.add(this.props.columnId[this.props.index])
                this.props.updateColumIdByChield(this.props.columnId)
            })
            // } 
        }

        if(this.props.councils != nextprops.councils && (Array.isArray(nextprops.councils) && nextprops.councils.length)){
            sortedCouncils = nextprops.councils.sort((a, b) => (a.councilNumber - b.councilNumber))
        }

        if (this.props.nationalAwards != nextprops.nationalAwards && Array.isArray(nextprops.nationalAwards)) {
            filteredNationalAwards = getFilteredNationalAwards(nextprops.nationalAwards);
        }
    }

    componentDidMount = () => {
        this.props.actions.getCommunityOrgTypes();
        this.props.actions.getCommunityOrgLookup();

        this.initializeFormFields();

        this.props.actions.getPhoneCountries();

        if (!this.props.councils && !this.props.council_loading ) {
            this.props.actions.getallcouncils();
        }

        if (!this.props.nationalAwards && !this.props.national_awards_loading) {
            this.props.actions.getNationalAwards();
        }

        if (!this.props.allstates && !this.props.statesLoading) {
            this.props.actions.getStates();
        }

        // No-ops when already loaded / in flight, so every filter row can call these safely.
        this.props.actions.getScoutingAssociationsLookup();
        this.props.actions.getAffinityGroupsLookup();
        if (this.props.columnId.length < 2 || !this.props.columnId.includes(this.props.columIdByChield[this.props.index])) {
            if (this.props.result && Object.keys(this.props.result).length > this.props.columIdByChield.length) {

                // uuid = 0 
                // Commented out to prevent all GROUP fields from sharing the same UUID (0).
                // to fix the issue reported on AAG-1265 => When there are multiple GROUP fields, and the UUID becomes 0, changing the first filter operand in the second GROUP unintentionally updates the first GROUP instead. 

                if (this.props.result[this.props.columnId[this.props.index]]) {
                    this.props.result[this.props.columnId[this.props.index]].map((data, i) => {
                        this.add(this.props.columnId[this.props.index])
                        this.props.updateColumIdByChield(this.props.columnId)
                    })
                } else if (this.props.columnId[this.props.index]){  // AAG-1265: While removing the existing 'GROUP' filter from the preSaved Filters  and adding a new 'GROUP' filter, ensure that the filter option is still displayed correctly.
                    this.add(this.props.columnId[this.props.index])
                    this.props.updateColumIdByChield(this.props.columnId)
                }
            } else {
                this.add(this.props.columnId[this.props.index])
                this.props.updateColumIdByChield(this.props.columnId)
            }

        }
        if (this.props.match.params.mode) {
            let ViewModeValue = this.props.match.params.mode
            let ViewMode = (ViewModeValue == 'v') ? true : false
            if (this.state.ViewMode == '')
                this.setState({
                    ViewMode: ViewMode
                })
        }
        //  else {
        //     this.add(this.props.columnId[this.props.index])
        //     this.props.updateColumIdByChield(this.props.columnId)
        // }
        //this.props.actions.getcompletecolumnlist()
        this.props.onRef(this)
    }
    componentWillUnmount() {
        this.props.onRef(undefined)
        //uuid = 0
    }

    reloadOnfield(data) {

        for (var key in data) {
            this.props.setFieldsValue({
                ["groupfilter" + key]: data[key]
            });
        }
    }


    getcolumns = () => {
        if (this.props.columnlist) {
            let columnlistArray = this.props.columnlist.DisplayColumnLists
            return columnlistArray.map((k, index) => {
                return (
                    <option key={index} value={Object.keys(columnlistArray[index]).toString().toLowerCase()}>{Object.values(columnlistArray[index]).toString().toLowerCase()}</option>
                )
            })
        }
    }

    // shouldComponentUpdate(nextprops){
    //     console.log("child is active")
    // }

    // componentDidUpdate(nextprops) {
    //     console.log("child is active")
    // }

    handleChange = (e, k, fieldid) => {

    let fieldIdArray = {
        [`${k}${fieldid}`]: e
    };

    // If this is the column dropdown
    if (fieldid === 2) {
        // Clear the filter value in local "fieldiddata" state
        fieldIdArray[`${k}3`] = "";
        fieldIdArray[`${k}4`] = "";

        this.props.setFieldsValue({
            [`filteroperator${k}`]: undefined,
            [`filtervalue${k}`]: undefined
        });
    }

    // If this is the operator dropdown
    if (fieldid === 3) {
        // Clear the filter value in local "fieldiddata" state
        fieldIdArray[`${k}4`] = "";

        this.props.setFieldsValue({
            [`filtervalue${k}`]: undefined
        });

        // // Clear local state used by the community-org-type two-select widget
        // this.setState(prev => ({
        //     selectedCommunityType: { ...prev.selectedCommunityType, [k]: undefined },
        //     selectedOrgName: { ...prev.selectedOrgName, [k]: undefined }
        // }));
    }

    this.props.updateFieldsValue(fieldIdArray)
}

    textinputhandleChange = (value, k, fieldid) => {
        // let value = event.target.value
        let ids = `${k}${fieldid}`
        let fieldIdArray = { [ids]: value }
        this.props.updateFieldsValue(fieldIdArray)
    }

    renderCouncilsOption = (forCSTRoles) => {
        if ((forCSTRoles && this.props.orgInfo && Array.isArray(this.props.orgInfo) && this.props.orgInfo.length > 0 && !this.props.orgInfoLoading) &&
        sortedCouncils && Array.isArray(sortedCouncils) && sortedCouncils.length > 0 && !this.props.council_loading) {
            return sortedCouncils.filter(council => this.props.orgInfo.some(org => org.organizationName === council.councilLong))
            .map(eachCouncil =>  <Option key={eachCouncil.sKCouncilId} value={eachCouncil.councilLong}>{eachCouncil.councilLong}</Option>)
        }
        else if (sortedCouncils && Array.isArray(sortedCouncils) && sortedCouncils.length > 0 && !this.props.council_loading ) {
            return sortedCouncils.map(eachCouncil =>  <Option key={eachCouncil.sKCouncilId} value={eachCouncil.councilLong}>{eachCouncil.councilLong}</Option>)
        }

    }

    renderEthnicity = () => {
        if (this.props.ethnicities && Array.isArray(this.props.ethnicities) && this.props.ethnicities.length > 0 && !this.props.ethnicities_loading ) {
            return this.props.ethnicities.map(ethnicity =>  <Option key={ethnicity.id} value={ethnicity.name}>{ethnicity.name}</Option>)
        }
    }

    renderStates = () => {
        if (this.props.allstates && Array.isArray(this.props.allstates) && this.props.allstates.length > 0 && !this.props.statesLoading) {
            return this.props.allstates.map(state => <Option key={state.id} value={state.short}>{state.name}</Option>)
        }
    }

    renderCommunityOrgTypes = () => {
        const { communityCategories } = this.props;

        if (!communityCategories) {
            return [<Option key="loading" disabled>Loading...</Option>];
        }

        if (communityCategories.length === 0) {
            return [<Option key="no-data" disabled>No Data</Option>];
        }

        return communityCategories.map(item => (
            <Option key={item.category_id} value={item.category_code}>
                {item.category_name}
            </Option>
        ));
    }

    renderLookupOptions = (k) => {
        const { lookupList, lookupLoading } = this.props;
        const selectedType = this.state.selectedCommunityType[k];

        console.log("selectedType FIXED:", selectedType);

        if (!selectedType) return [];

        if (lookupLoading) {
            return [<Option key="loading">Loading...</Option>];
        }

        const filtered = (lookupList || []).filter(
            item =>
                item.short &&
                item.short.toLowerCase() === selectedType.toLowerCase()
        );

        console.log("Filtered:", filtered);

        if (filtered.length === 0) {
            return [<Option key="no-data">No Data</Option>];
        }

        return filtered.map(item => (
            <Option key={item.id} value={item.name}>
                {item.name}
            </Option>
        ));
    };

    componentDidUpdate(prevProps) {
        console.log("child is active")
        
        if (prevProps.fieldiddata !== this.props.fieldiddata || prevProps.groupids !== this.props.groupids) {
          this.initializeFormFields();
        }
      }
      
    initializeFormFields = () => {
        const { columnId, index, getFieldDecorator, getFieldValue } = this.props;
        const columnKey = columnId && columnId[index];
        if (!columnKey) return;
      
        getFieldDecorator("groupfilter" + columnKey, { initialValue: [] });
        let groupfilter = getFieldValue("groupfilter" + columnKey) || [];
      
        if (groupfilter.length === 0 && this.props.groupids && this.props.groupids[columnKey]) {
          this.props.setFieldsValue({
            ["groupfilter" + columnKey]: this.props.groupids[columnKey]
          });
        }
    };      

    // Active, name-sorted <Option>s; memoized so re-renders reuse the same elements until the list changes.
    buildLookupOptions = memoizeOne((list) => {
        const today = moment().format('YYYY-MM-DD');
        return (list || [])
            .filter(item => item && item.name && (!item.expiryDt || item.expiryDt >= today))
            .map(item => item.name.trim())
            .sort((a, b) => a.localeCompare(b))
            .map(name => <Option key={name} value={name}>{name}</Option>);
    });

    renderLookupOptions = (list, loading) => {
        if (loading && !list) return <Option key="loading" value="" disabled>Loading...</Option>;
        return this.buildLookupOptions(list);
    }

    // Same list as buildLookupOptions, but each option submits the lookup ID and shows the name.
    buildLookupIdOptions = memoizeOne((list) => {
        const today = moment().format('YYYY-MM-DD');
        return (list || [])
            .filter(item => item && item.name && getLookupId(item) !== undefined && (!item.expiryDt || item.expiryDt >= today))
            .sort((a, b) => a.name.trim().localeCompare(b.name.trim()))
            .map(item => <Option key={getLookupId(item)} value={String(getLookupId(item))}>{item.name.trim()}</Option>);
    });

    renderLookupIdOptions = (list, loading) => {
        if (loading && !list) return <Option key="loading" value="" disabled>Loading...</Option>;
        return this.buildLookupIdOptions(list);
    }

    render() {
        const { getFieldDecorator, getFieldValue, columnId, columIdByChield, result, groupids, fieldiddata, index } = this.props;
        const columnKey = columnId && columnId[index];

        console.log(uuid);
        console.log(columIdByChield);
        console.log(result);
        console.log(groupids);

        if (!columnKey) return null;

        getFieldDecorator("groupfilter" + columnKey, { initialValue: [] });
        var groupfilter = getFieldValue("groupfilter" + columnKey);

        if (!groupfilter || groupfilter.length === 0) {
            groupfilter = [];
        }

        const formItems = groupfilter.map((k, groupIndex) => {
            
            {getFieldDecorator("filtervalue" + k, {
            initialValue: this.state.selectedCommunityType[k] || ''
            })(
                <Input type="hidden" />
            )}

            {getFieldDecorator("communityorganizationname_" + k, {
            initialValue: this.state.selectedOrgName[k] || ''
            })(
                <Input type="hidden" />
            )}
            return (
                <Draggable draggableId={`fieldsId:${k}`} key={k} index={groupIndex}>
                    {(provided) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                        >
                            {index !== 0 && groupIndex === 0 && (
                                <Row>
                                    <Col span={24}>
                                        <div className='group-operator'>
                                            <label className='inner-scope inner-scope-blue'> AND </label>
                                            {/* <FormItem hasFeedback>  //AAG-1264 disabled scope operator
                                                {console.log(`groupoperator${k}`, fieldiddata[`${k}1`])}
                                                {getFieldDecorator("groupoperator" + k, {
                                                    validateTrigger: ['onChange', 'onBlur'],
                                                    initialValue: fieldiddata && fieldiddata[k + "1"] ? fieldiddata[k + "1"] : '',
                                                    rules: [{
                                                        required: true
                                                    }],
                                                })(
                                                    <Select 
                                                    disabled={this.state.ViewMode} 
                                                    className='scope' onChange={(e) => this.handleChange(e, k, 1)}>
                                                        <Option value="AND">AND</Option>
                                                        <Option value="OR">OR</Option>
                                                        <Option value="AND NOT">AND NOT</Option>
                                                        <Option value="OR NOT">OR NOT</Option>
                                                    </Select>
                                                )}
                                            </FormItem> */}
                                        </div>
                                    </Col>
                                </Row>
                            )}
                            <div className='list-filter'>
                                <div className='filterby'>
                                    {groupIndex >= 1 ? <div className='drag-icon'><i {...provided.dragHandleProps} className="fa fa-lg fa-ellipsis-v"></i></div> : <div {...provided.dragHandleProps}> </div>}
                                    {groupIndex == 0 ? <label className='inner-scope'> SCOPE </label> : <label className='inner-scope inner-scope-blue'> AND </label>
                                        // <FormItem hasFeedback> //AAG-1264 disabled scope operator
                                        //     {getFieldDecorator("filterscope" + k, {
                                        //         validateTrigger: ['onChange', 'onBlur'],
                                        //         initialValue: fieldiddata && fieldiddata[k + "1"] ? fieldiddata[k + "1"] : '',
                                        //         rules: [{
                                        //             required: true
                                        //         }],
                                        //     })(
                                        //         <Select 
                                        //         disabled={this.state.ViewMode}
                                        //         className='scope' onChange={(e) => this.handleChange(e, k, 1)}>
                                        //             <Option value="AND">AND</Option>
                                        //             <Option value="OR">OR</Option>
                                        //             <Option value="AND NOT">AND NOT</Option>
                                        //             <Option value="OR NOT">OR NOT</Option>
                                        //         </Select>
                                        //     )}

                                        // </FormItem>
                                    }
                                    <FormItem hasFeedback>
                                        {getFieldDecorator("filtercolumn" + k, {
                                            validateTrigger: ['onChange', 'onBlur'],
                                            initialValue: fieldiddata && fieldiddata[k + "2"] ? fieldiddata[k + "2"] : '',
                                            rules: [{
                                                required: true
                                            }],
                                        })(
                                            <Select disabled={this.state.ViewMode}
                                                optionLabelProp="label"
                                                className='filterby-element'
                                                showSearch
                                                optionFilterProp="label"
                                                filterOption={(input, option) =>
                                                    option.props.label && option.props.label.toLowerCase().replace(/\s/g, '').includes(input.toLowerCase())
                                                  }
                                                onChange={(e) => {
                                                    this.handleChange(e, k, 2)
                                                    // let affiliationKeys = [...this.state.scoutingAffiliationKey, index]
                                                    // const affinityKeys = [...this.state.affinityGroupsKey, index]
                                                    // if (e == 'scoutingaffiliation') {
                                                    //     console.log("Scouting Affiliation", e);
                                                    //     this.setState({
                                                    //         scoutingAffiliationKey: affiliationKeys
                                                    //     })
                                                    // }
                                                    // else if (e === 'affinitygroup') {
                                                    //     console.log('Affinity Group', e);

                                                    //     this.setState({
                                                    //         affinityGroupsKey: affinityKeys
                                                    //     })
                                                    //     console.log('Affinity Keys', affinityKeys);
                                                    // }
                                                    // else {
                                                    //     if (affiliationKeys.includes(index)) {
                                                    //         let getFilteredAffilitations = affiliationKeys.filter(e => {
                                                    //             e != index
                                                    //         })
                                                    //         this.setState({
                                                    //             scoutingAffiliationKey: getFilteredAffilitations
                                                    //         })
                                                    //     }
                                                    //     if (affinityKeys.includes(index)) {
                                                    //         let getFilteredAffinityKeys = affinityKeys.filter(e => {
                                                    //             e != index
                                                    //         })
                                                    //         this.setState({
                                                    //             affinityGroupsKey: getFilteredAffinityKeys
                                                    //         })
                                                    //     }
                                                    // }
                                                }}
                                            >
                                                {/* {this.getcolumns()} */}
                                                {filteredColumns.map(col => (
                                                    <Option
                                                    key={col}
                                                    value={col.toLowerCase()}
                                                    label={getColumnLabel(col)}
                                                    title={getColumnLabel(col)}>
                                                        {/* <Tooltip title={col}> */}
                                                            {/* <span className="truncate-option">{col.toLowerCase() == 'skpersonid' ? "memberID" : col}</span> */}
                                                            <span className="truncate-option">
                                                                {
                                                                    col.toLowerCase() === "skpersonid"
                                                                        ? "MemberID"
                                                                        : col.toLowerCase() === "communityorganizationcategorycode"
                                                                        ? "Community Organization Type and Name"
                                                                        : getColumnLabel(col)
                                                                }
                                                            </span>
                                                        {/* </Tooltip> */}
                                                    </Option>
                                                )).filter(col => {
                                                    if ((this.props.isCSTSupport || this.props.isCouncilCommittee) && col.props.value === 'councilaffiliation') {
                                                        return false;
                                                    }
                                                    
                                                    return true;
                                                })}
                                            </Select>
                                        )}
                                    </FormItem>
                                </div>
                                <div className='line'></div>
                                <div className='operator'>
                                    <FormItem hasFeedback>
                                        {getFieldDecorator("filteroperator" + k, {
                                            validateTrigger: ['onChange', 'onBlur'],
                                            initialValue: fieldiddata && typeof fieldiddata[k + "3"] == 'string' ? normalizeIncomingOperator(fieldiddata[k + "3"].toLowerCase().replace(/\s/g, '')) : '',
                                            rules: [{ required: true }],
                                        })(
                                            <Select
                                                className='filterby-element'
                                                onChange={(e) => this.handleChange(e, k, 3)}
                                                disabled={this.state.ViewMode}
                                            >
                                                {getOperatorsForColumn(fieldiddata && fieldiddata[`${k}2`], columnOperatorMap)
                                                    .map(op => {
                                                        if (op.toLowerCase() === 'inlist') {
                                                            return <Option value={"INLIST"}>{normalizeIncomingOperator(op.toLowerCase().replace(/\s/g, ''))}&nbsp;
                                                                <Tooltip placement="top" title="Enter multiple values(Comma-Separated) to match any (OR) of them.">
                                                                    <Icon type="info-circle-o" />
                                                                </Tooltip>
                                                            </Option>
                                                        }
                                                        else if (op.toLowerCase() === 'range') {
                                                            return <Option value={"_range"}>{normalizeIncomingOperator(op.toLowerCase().replace(/\s/g, ''))}&nbsp;
                                                                <Tooltip placement="top" title="Enter single values or ranges separated by commas. Use a hyphen (-) between range values. Example: 10000-50000,35215,99999">
                                                                    <Icon type="info-circle-o" />
                                                                </Tooltip>
                                                            </Option>
                                                        }
                                                        else if (op.toLowerCase() === 'does not match') {
                                                            // Value stays "DoesNotMatch" so the backend filter suffix is unchanged;
                                                            // only the displayed label differs.
                                                            return <Option key={op} value={op}>
                                                                Not Equal to
                                                            </Option>
                                                        }
                                                        else {
                                                            return <Option key={op} value={op}>
                                                                {normalizeIncomingOperator(op.toLowerCase().replace(/\s/g, ''))}
                                                            </Option>
                                                        }
                                                    }
                                                    )}
                                            </Select>
                                        )}
                                    </FormItem>
                                </div>
                                <div className='line'></div>
                                <div className='value'>
                                    {fieldiddata[k + "3"] == "NBV" || fieldiddata[k + "3"] == "BV" ?
                                    null
                                    // <FormItem hasFeedback>
                                    //                     {getFieldDecorator("filtervalue" + k)(
                                    //                         <Input disabled placeholder='Value' />
                                    //                     )}
                                    //                 </FormItem>
                                    
                                        : ((fieldiddata[`${k}2`] && (fieldiddata[`${k}2`].includes('date') || fieldiddata[`${k}2`].includes('dt'))) && (fieldiddata[k + "3"]
                                            && fieldiddata[k + "3"].toLowerCase() == "_range")
                                        ) ?
                                            <FormItem hasFeedback key={`range-${k}`}>
                                                {getFieldDecorator("filtervalue" + k, {
                                                    rules: [{ required: true, message: "*Required" }],
                                                })(
                                                    <RangePicker
                                                        format="YYYY-MM-DD"
                                                        placeholder={["YYYY-MM-DD", "YYYY-MM-DD"]}
                                                        disabled={this.state.ViewMode}
                                                        className="ant-input"
                                                        value={
                                                            fieldiddata && fieldiddata[k + "4"]
                                                                ? (Array.isArray(fieldiddata[k + "4"])
                                                                    ? fieldiddata[k + "4"].map(d => moment(d, "YYYY-MM-DD"))
                                                                    : fieldiddata[k + "4"].split(" - ").map(d => moment(d.trim(), "YYYY-MM-DD")))
                                                                : []
                                                        }
                                                        onChange={(dates, dateStrings) => {
                                                            const value =
                                                                dateStrings && dateStrings[0] && dateStrings[1]
                                                                    ? `${dateStrings[0]} - ${dateStrings[1]}`
                                                                    : '';

                                                            this.textinputhandleChange(value, k, 4);

                                                            // manually sync Form's internal field value so validation passes
                                                            this.props.setFieldsValue({
                                                                ["filtervalue" + k]: value
                                                            });
                                                        }}
                                                    />
                                                )}
                                            </FormItem>
                                   : ( (fieldiddata[`${k}2`] && (fieldiddata[`${k}2`].includes('date') || fieldiddata[`${k}2`].includes('dt'))) && (fieldiddata[k + "3"] 
                                    // && (fieldiddata[k + "3"] !== "NOTNULL" && fieldiddata[k + "3"] !== "NE"  
                                    // && fieldiddata[k + "3"] !== "NULL")
                                    )) ?
                                        <FormItem hasFeedback >
                                            {getFieldDecorator("filtervalue" + k, {
                                                validateTrigger: ['onChange', 'onBlur'],
                                                initialValue: (() => {
                                                    const value = fieldiddata && fieldiddata[k + "4"];

                                                    if (typeof value !== "string") return "";

                                                    const date = value.includes(" - ")
                                                        ? value.split(" - ")[0].trim()
                                                        : value;

                                                    return moment(date, "YYYY-MM-DD", true).isValid()
                                                        ? date
                                                        : "";
                                                })(),
                                                rules: [{
                                                    required: true,
                                                    message: "*Required",
                                                },
                                                {
                                                    pattern: /^(19|20)\d{2}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/,
                                                    message: `Please enter correct format YYYY/MM/DD Pattern`,
                                                }
                                            ],
                                            })(
                                                <InputMask
                                                    mask="9999-99-99"
                                                    placeholder="YYYY-MM-DD"
                                                    alwaysShowMask
                                                    maskChar={null}
                                                    className="ant-input"
                                                    disabled={this.state.ViewMode}
                                                    onChange={(value) => this.textinputhandleChange(value, k, 4)}
                                                    render={(ref, props) => <Input {...props} ref={ref} />}
                                            />
                                            )}
                                        </FormItem>
                                    : (fieldiddata[`${k}2`] && (fieldiddata[`${k}2`].toLowerCase() == 'phonenumber'
                                    // && (fieldiddata[k + "3"] !== "NOTNULL" && fieldiddata[k + "3"] !== "NE"  
                                    // && fieldiddata[k + "3"] !== "NULL")
                                    )) ?
                                            <FormItem hasFeedback  >
                                                {/* Phone Number */}
                                                {getFieldDecorator("filtervalue" + k, {
                                                    validateTrigger: ['onChange', 'onBlur'],
                                                    initialValue:
                                                        fieldiddata && fieldiddata[k + "4"]
                                                            ? fieldiddata[k + "4"]
                                                            : '',
                                                    rules: isInListOperator(fieldiddata, k) ? [
                                                        { required: true, message: '*Required' },
                                                        { pattern: /^[0-9]{7,15}(\s*,\s*[0-9]{7,15})*$/, message: 'Enter comma-separated phone numbers, each 7-15 digits' }
                                                    ] : (['equals', 'doesnotmatch', 'eq', 'ne'].includes(String(fieldiddata[k + "3"] || '').toLowerCase().replace(/\s/g, ''))) ? [
                                                        // Exact match operators need a full phone number
                                                        { required: true, message: '*Required' },
                                                        { pattern: /^[0-9]*$/, message: 'Enter only numeric!' },
                                                        { min: 7, max: 15, message: 'Must be between 7 and 15 digits!' }
                                                    ] : [
                                                        { required: true, message: '*Required' },
                                                        { pattern: /^[0-9]*$/, message: 'Enter only numeric!' }
                                                    ],
                                                })(
                                                    <Input
                                                     style={{ width: 250, }}
                                                        placeholder={isInListOperator(fieldiddata, k) ? "Comma-separated phone numbers" : "Phone Number"}
                                                        // onChange={() => this.getUpdatedPhoneKey(k)}
                                                    />
                                                )}

                                            </FormItem>
                                    : (fieldiddata[`${k}2`] && ['skpersonid', 'personid', 'prsonguid'].includes(fieldiddata[`${k}2`].toLowerCase())) ?
                                        <FormItem hasFeedback>
                                            {getFieldDecorator("filtervalue" + k, {
                                                validateTrigger: ['onChange', 'onBlur'],
                                                initialValue: fieldiddata && fieldiddata[k + "4"] ? fieldiddata[k + "4"] : '',
                                                rules: [
                                                    { required: true, message: "*Required" },
                                                    fieldiddata[`${k}2`].toLowerCase() === 'prsonguid'
                                                        ? { pattern: /^[0-9A-Fa-f-]*$/, message: "PersonGUID must contain only letters, numbers, and hyphens" }
                                                        : { pattern: /^[0-9]*$/, message: `${fieldiddata[`${k}2`].toLowerCase() === 'skpersonid' ? 'MemberID' : 'PersonID'} must contain numbers only` }
                                                ],
                                            })(
                                                <Input
                                                    disabled={this.state.ViewMode}
                                                    placeholder='Value'
                                                    onChange={(event) => this.textinputhandleChange(event.target.value, k, 4)}
                                                />
                                            )}
                                        </FormItem>
                                    :
                                    (fieldiddata[`${k}2`] && BooleanFilters.includes(fieldiddata[`${k}2`])) ?
                                        <FormItem hasFeedback>
                                            {getFieldDecorator("filtervalue" + k, {
                                                validateTrigger: ['onChange', 'onBlur'],
                                                initialValue: fieldiddata && fieldiddata[k + "4"] ? fieldiddata[k + "4"] : '',
                                                rules: [{
                                                    required: true,
                                                    message: "*Required",
                                                }],
                                            })(
                                                <Select
                                                    showSearch
                                                    placeholder="Affiliations"
                                                    className='filterby-element'
                                                    disabled={this.state.ViewMode}
                                                    onChange={(value) => this.textinputhandleChange(value, k, 4)}
                                                >
                                                    <Option value="1">True</Option>
                                                    <Option value="0">False</Option>
                                                </Select>
                                            )}
                                        </FormItem>
                                    : 
                                    (fieldiddata[`${k}2`] == 'scoutingaffiliation' && (fieldiddata[k + "3"] 
                                    && (fieldiddata[k + "3"] !== "NOTNULL" && fieldiddata[k + "3"] !== "NE"  
                                    && fieldiddata[k + "3"] !== "NULL")
                                    )) ?
                                        <FormItem hasFeedback>
                                            {getFieldDecorator("filtervalue" + k, {
                                                validateTrigger: ['onChange', 'onBlur'],
                                                initialValue: getFilterValueInitial(fieldiddata, k),
                                                rules: [{
                                                    required: true,
                                                    message: "*Required",
                                                }],
                                            })(
                                                <Select
                                                    showSearch
                                                    mode={isInListOperator(fieldiddata, k) ? "multiple" : undefined}
                                                    allowClear={isInListOperator(fieldiddata, k)}
                                                    placeholder="Affiliations"
                                                    optionFilterProp="children"
                                                    className='filterby-element'
                                                    disabled={this.state.ViewMode}
                                                    onChange={(value) => this.textinputhandleChange(value, k, 4)}
                                                >
                                                    {usesIdValue(fieldiddata, k)
                                                        ? this.renderLookupIdOptions(this.props.scoutingAssociations, this.props.scoutingAssociationsLoading)
                                                        : this.renderLookupOptions(this.props.scoutingAssociations, this.props.scoutingAssociationsLoading)}
                                                </Select>
                                            )}
                                        </FormItem>
                                        : (
                                            (
                                                (fieldiddata[k + "2"] === 'councilaffiliation' && !(this.props.isCSTSupport || this.props.isCouncilCommittee)) ||
                                                (typeof fieldiddata[k + "2"] === 'string' &&( fieldiddata[k + "2"].includes('councilname') || fieldiddata[k + "2"].includes('councilnumber') 
                                            ))
                                        )  && (fieldiddata[k + "3"]
                                                && (fieldiddata[k + "3"] !== "NOTNULL" && fieldiddata[k + "3"] !== "NE" 
                                                    && fieldiddata[k + "3"] !== "NULL")
                                            )) ?
                                        <FormItem hasFeedback>
                                            {getFieldDecorator("filtervalue" + k, {
                                                validateTrigger: ['onChange', 'onBlur'],
                                                initialValue: getFilterValueInitial(fieldiddata, k),
                                                rules: [{
                                                    required: true,
                                                    message: "*Required",
                                                }],
                                            })(
                                                <Select
                                                    showSearch
                                                    mode={isInListOperator(fieldiddata, k) ? "multiple" : undefined}
                                                    allowClear={isInListOperator(fieldiddata, k)}
                                                    placeholder="Affiliations"
                                                    className='filterby-element'
                                                    disabled={this.state.ViewMode}
                                                    onChange={(value) => {
                                                        if(fieldiddata[`${k}2`] == 'councilnumber'){
                                                            councilNumberFieldKey = k
                                                            this.props.checkCouncilNumberSelectedHandler(true)
                                                        }
                                                        this.textinputhandleChange(value, k, 4)
                                                    }
                                                }
                                                >
                                                   {this.renderCouncilsOption( (fieldiddata[`${k}2`] == 'councilnumber' && this.props.isCSTSupport) )}
                                                </Select>
                                            )}
                                        </FormItem>
                                        : (fieldiddata[`${k}2`] == 'affinitygroup' && (fieldiddata[k + "3"] 
                                        && (fieldiddata[k + "3"] !== "NOTNULL" && fieldiddata[k + "3"] !== "NE"  
                                        && fieldiddata[k + "3"] !== "NULL")
                                        )) ? <FormItem hasFeedback>
                                            {getFieldDecorator("filtervalue" + k, {
                                                validateTrigger: ['onChange', 'onBlur'],
                                                initialValue: getFilterValueInitial(fieldiddata, k),
                                                rules: [{
                                                    required: true,
                                                    message: "*Required",
                                                }],
                                            })(
                                                <Select
                                                    showSearch
                                                    mode={isInListOperator(fieldiddata, k) ? "multiple" : undefined}
                                                    allowClear={isInListOperator(fieldiddata, k)}
                                                    placeholder="Affiliations"
                                                    optionFilterProp="children"
                                                    className='filterby-element'
                                                    disabled={this.state.ViewMode}
                                                    onChange={(value) => this.textinputhandleChange(value, k, 4)}
                                                >
                                                    {usesIdValue(fieldiddata, k)
                                                        ? this.renderLookupIdOptions(this.props.affinityGroups, this.props.affinityGroupsLoading)
                                                        : this.renderLookupOptions(this.props.affinityGroups, this.props.affinityGroupsLoading)}
                                                </Select>
                                            )}
                                        </FormItem>
                                            : (fieldiddata[`${k}2`] == 'membershipstatus'  && (fieldiddata[k + "3"] 
                                            && (fieldiddata[k + "3"] !== "NOTNULL" && fieldiddata[k + "3"] !== "NE"  
                                            && fieldiddata[k + "3"] !== "NULL")
                                            )) ? <FormItem hasFeedback>
                                            {getFieldDecorator("filtervalue" + k, {
                                                validateTrigger: ['onChange', 'onBlur'],
                                                initialValue: getFilterValueInitial(fieldiddata, k),
                                                rules: [{
                                                    required: true,
                                                    message: "*Required",
                                                }],
                                            })(
                                                <Select
                                                    showSearch
                                                    mode={isInListOperator(fieldiddata, k) ? "multiple" : undefined}
                                                    allowClear={isInListOperator(fieldiddata, k)}
                                                    placeholder="membershipStatus"
                                                    className='filterby-element'
                                                    disabled={this.state.ViewMode}
                                                    // onChange={(value) => this.textinputhandleChange(value, k, 4)}
                                                >
                                                    <Option value="active">Active</Option>
                                                    <Option value="expired">Expired</Option>
                                                    <Option value="inActive">Inactive</Option>
                                                    <Option value="lapsed">Lapsed</Option>
                                                    <Option value="upgrade">Upgrade</Option>
                                                </Select>
                                            )}
                                        </FormItem>
                                                : (fieldiddata[`${k}2`] == 'membershiptype'  && (fieldiddata[k + "3"] 
                                                && (fieldiddata[k + "3"] !== "NOTNULL" && fieldiddata[k + "3"] !== "NE"  
                                                && fieldiddata[k + "3"] !== "NULL")
                                                )) ? <FormItem hasFeedback>
                                                    {getFieldDecorator("filtervalue" + k, {
                                                        validateTrigger: ['onChange', 'onBlur'],
                                                        initialValue: fieldiddata && fieldiddata[k + "4"] ? fieldiddata[k + "4"] : '',
                                                        rules: [{
                                                            required: true,
                                                            message: "*Required",
                                                        }],
                                                    })(
                                                        <Select
                                                            showSearch
                                                            placeholder="membershipType"
                                                            className='filterby-element'
                                                            disabled={this.state.ViewMode}
                                                        // onChange={(value) => this.textinputhandleChange(value, k, 4)}
                                                        >
                                                            <Option value="5Y">5Year</Option>
                                                            <Option value="LT">Lifetime</Option>
                                                        </Select>
                                                    )}
                                                </FormItem>
                                                : (fieldiddata[`${k}2`] == 'socialmedia'  && (fieldiddata[k + "3"] 
                                                && (fieldiddata[k + "3"] !== "NOTNULL" && fieldiddata[k + "3"] !== "NE"  
                                                && fieldiddata[k + "3"] !== "NULL")
                                                )) ? <FormItem hasFeedback>
                                                    {getFieldDecorator("filtervalue" + k, {
                                                        validateTrigger: ['onChange', 'onBlur'],
                                                        initialValue: fieldiddata && fieldiddata[k + "4"] ? fieldiddata[k + "4"] : '',
                                                        rules: [{
                                                            required: true,
                                                            message: "*Required",
                                                        }],
                                                    })(
                                                        <Select
                                                            showSearch
                                                            placeholder="socialmedia"
                                                            className='filterby-element'
                                                            disabled={this.state.ViewMode}
                                                        // onChange={(value) => this.textinputhandleChange(value, k, 4)}
                                                        >
                                                            <Option value="facebookname">Facebook</Option>
                                                            <Option value="instagramname">Instagram</Option>
                                                            <Option value="linkedinname">LinkedIn</Option>
                                                            <Option value="twittername">Twitter</Option>
                                                        </Select>
                                                    )}
                                                </FormItem>
                                                : (fieldiddata[`${k}2`] == 'nationalawardid'  && (fieldiddata[k + "3"]
                                                && (fieldiddata[k + "3"] !== "NOTNULL" && fieldiddata[k + "3"] !== "NE"
                                                && fieldiddata[k + "3"] !== "NULL")
                                                )) ? <FormItem hasFeedback>
                                                    {getFieldDecorator("filtervalue" + k, {
                                                        validateTrigger: ['onChange', 'onBlur'],
                                                        initialValue: getFilterValueInitial(fieldiddata, k),
                                                        rules: [{
                                                            required: true,
                                                            message: "*Required",
                                                        }],
                                                    })(
                                                        <Select
                                                            showSearch
                                                            mode={isInListOperator(fieldiddata, k) ? "multiple" : undefined}
                                                            allowClear={isInListOperator(fieldiddata, k)}
                                                            placeholder="National Award"
                                                            className='filterby-element'
                                                            disabled={this.state.ViewMode}
                                                        // onChange={(value) => this.textinputhandleChange(value, k, 4)}
                                                        >
                                                            {filteredNationalAwards.map(nationalAward => <Option key={nationalAward.id} value={nationalAward.id}>{nationalAward.awardLong}</Option>)}

                                                        </Select>
                                                    )}
                                                </FormItem>
                                                : (fieldiddata[`${k}2`] == 'ethnicity'  && (fieldiddata[k + "3"]
                                                && (fieldiddata[k + "3"] !== "NOTNULL" && fieldiddata[k + "3"] !== "NE"
                                                && fieldiddata[k + "3"] !== "NULL")
                                                )) ? <FormItem hasFeedback>
                                                    {getFieldDecorator("filtervalue" + k, {
                                                        validateTrigger: ['onChange', 'onBlur'],
                                                        initialValue: getFilterValueInitial(fieldiddata, k),
                                                        rules: [{
                                                            required: true,
                                                            message: "*Required",
                                                        }],
                                                    })(
                                                        <Select
                                                            showSearch
                                                            mode={isInListOperator(fieldiddata, k) ? "multiple" : undefined}
                                                            allowClear={isInListOperator(fieldiddata, k)}
                                                            placeholder="socialmedia"
                                                            className='filterby-element'
                                                            disabled={this.state.ViewMode}
                                                        // onChange={(value) => this.textinputhandleChange(value, k, 4)}
                                                        >
                                                           {this.renderEthnicity()}

                                                        </Select>
                                                    )}
                                                </FormItem>
                                                : (typeof fieldiddata[`${k}2`] === 'string' && fieldiddata[`${k}2`].toLowerCase().includes('state') && (fieldiddata[k + "3"]
                                                && (fieldiddata[k + "3"] !== "NOTNULL" && fieldiddata[k + "3"] !== "NE"
                                                && fieldiddata[k + "3"] !== "NULL")
                                                )) ? <FormItem hasFeedback>
                                                    {getFieldDecorator("filtervalue" + k, {
                                                        validateTrigger: ['onChange', 'onBlur'],
                                                        initialValue: getFilterValueInitial(fieldiddata, k),
                                                        rules: [{
                                                            required: true,
                                                            message: "*Required",
                                                        }],
                                                    })(
                                                        <Select
                                                            showSearch
                                                            mode={isInListOperator(fieldiddata, k) ? "multiple" : undefined}
                                                            allowClear={isInListOperator(fieldiddata, k)}
                                                            placeholder="State"
                                                            className='filterby-element'
                                                            loading={this.props.statesLoading}
                                                            disabled={this.state.ViewMode}
                                                            onChange={(value) => this.textinputhandleChange(value, k, 4)}
                                                        >
                                                            {this.renderStates()}

                                                        </Select>
                                                    )}
                                                </FormItem>
                                                : (fieldiddata[`${k}2`] == 'communityorganizationcategorycode') ?
                                                <div className="community-org-wrapper">

                                                    <Select
                                                        value={this.state.selectedCommunityType[k] || undefined}
                                                        placeholder="Community Organization Type"
                                                        className="community-select"
                                                        disabled={this.state.ViewMode}
                                                        onChange={(value) => {

                                                            this.props.setFieldsValue({
                                                                [`filtervalue${k}`]: value
                                                            });

                                                            this.textinputhandleChange(value, k, 4);

                                                            this.setState(prev => ({
                                                                selectedCommunityType: {
                                                                    ...prev.selectedCommunityType,
                                                                    [k]: value
                                                                },
                                                                selectedOrgName: {
                                                                    ...prev.selectedOrgName,
                                                                    [k]: undefined
                                                                }
                                                            }));
                                                            this.props.setFieldsValue({
                                                                ["filtervalue" + k]: value,
                                                                ["communityorganizationname_" + k]: undefined
                                                            });
                                                        }}
                                                    >
                                                        {this.renderCommunityOrgTypes()}
                                                    </Select>

                                                    <Select
                                                        value={this.state.selectedOrgName[k] || undefined}
                                                        placeholder="Select organization name"
                                                        className="community-input"
                                                        loading={this.props.lookupLoading}
                                                        disabled={!this.state.selectedCommunityType[k] || this.state.ViewMode}
                                                        onChange={(value) => {

                                                            this.props.setFieldsValue({
                                                                [`communityorganizationname_${k}`]: value
                                                            });

                                                            this.setState(prev => ({
                                                                selectedOrgName: {
                                                                    ...prev.selectedOrgName,
                                                                    [k]: value
                                                                }
                                                            }));
                                                            this.props.setFieldsValue({
                                                                ["communityorganizationname_" + k]: value
                                                            });
                                                        }}
                                                    >
                                                        {this.renderLookupOptions(k)}
                                                    </Select>

                                                </div>
                                                : ((fieldiddata[k + "3"] !== "NOTNULL" && fieldiddata[k + "3"] !== "NE"  && fieldiddata[k + "3"] !== "NULL")) ?
                                                    <FormItem hasFeedback>
                                                        {getFieldDecorator("filtervalue" + k, {
                                                            validateTrigger: ['onChange', 'onBlur'],
                                                            initialValue: fieldiddata && fieldiddata[k + "4"] ? fieldiddata[k + "4"] : '',
                                                            rules: [{
                                                                required: true,
                                                                message: "*Required",
                                                            }],
                                                        })(
                                                            <Input disabled={this.state.ViewMode} placeholder='Value' onChange={(event) => this.textinputhandleChange(event.target.value, k, 4)} />
                                                        )}
                                                    </FormItem>
                                                    : null
                                    }
                                </div>
                                {groupIndex >= 1 && (!this.state.ViewMode) ?
                                    <FormItem>
                                        <div className='remove-icon'>
                                            <Tooltip placement="top" title="Remove this filter">
                                                <Icon onClick={() => this.remove(columnId[index], k)} type="close" />
                                            </Tooltip>
                                        </div>
                                    </FormItem>
                                    : ' '
                                }
                            </div>
                        </div>
                    )}
                </Draggable>
            )
        });

        return (
            <Droppable droppableId={columnKey} type='fields'>
                {(provided) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                    >
                        <div className="panel-filter query-builder">
                            {formItems}
                            <div className='add-filter-panel'>
                                {!this.state.ViewMode && <span className='pointer' onClick={() => this.add(columnKey)}><Icon type="plus-circle-o" /> Add Filter</span>}
                            </div>
                        </div>
                    </div>
                )}
            </Droppable>
        );
    }
}


function mapStateToProps(state) {
    return {
        profileinfo: state.profilereducer.profileinfo,

        columnlist: state.columnlistreducer.columnlist,
        preferencedata: state.getpreferencedatareducer.preferencedata,
        maxRecords: state.getpreferencedatareducer.maxRecords,
        councils: state.councilsreducer.councils,
        council_loading: state.councilsaddreducer.loading,
        nationalAwards: state.nationalAwardsReducer.nationalAwards,
        national_awards_loading: state.nationalAwardsReducer.loading,
        ethnicities_loading: state.ethnicitiesreducer.loading,
        ethnicities: state.ethnicitiesreducer.ethnicities,
        isCSTSupport: state.isadminreducer.isCSTSupport,
        isCouncilCommittee: state.isadminreducer.isCouncilCommittee,
        
        getCSTRoleOrg: state.isadminreducer.getCSTRoleOrg,

        orgInfoLoading: state.childOrganzationReducer.loading,
        orgInfo: state.childOrganzationReducer.orgInfo,
        orgInfoError: state.childOrganzationReducer.error,

        positionLoading: state.assignRolesPositionsReducer.loading,
        positionList: state.assignRolesPositionsReducer.positionList,
        communityCategories: state.communityOrgTypesReducer.categories,
        

        lookupList: state.communityLookupReducer ? state.communityLookupReducer.lookupList : [],
        lookupLoading: state.communityLookupReducer ? state.communityLookupReducer.loading : false,
        lookupError: state.communityLookupReducer ? state.communityLookupReducer.error : null,

        scoutingAssociations: (state.alumniLookupsReducer.scoutingAssociations || {}).list,
        scoutingAssociationsLoading: (state.alumniLookupsReducer.scoutingAssociations || {}).loading,
        affinityGroups: (state.alumniLookupsReducer.affinityGroups || {}).list,
        affinityGroupsLoading: (state.alumniLookupsReducer.affinityGroups || {}).loading,

        allstates: state.allstatesreducer.states,
        statesLoading: state.allstatesreducer.loading,
    }
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(actions, dispatch)
    }
}

const Groupfield = Form.create()(Groupfields);

export default compose(connect(mapStateToProps, mapDispatchToProps), withRouter)(Groupfield);



// WEBPACK FOOTER //
// ./src/components/ListandAdvancedSearch/GroupFields.js